import { motion } from 'framer-motion';
import { useContent } from '../contexts/ContentContext';
import { FaGraduationCap, FaHeart, FaRegLightbulb, FaWhatsapp } from 'react-icons/fa';
import aboutImage from '../assets/about-image.jpg';

const About = () => {
  const { content } = useContent();
  const aboutContent = content.about || {};

  const highlights = [
    {
      icon: <FaGraduationCap />,
      title: 'Formação',
      description: 'Psicóloga formada com especialização em Terapia Cognitivo-Comportamental'
    },
    {
      icon: <FaHeart />,
      title: 'Experiência',
      description: 'Anos de prática clínica com atendimento humanizado e acolhedor'
    },
    {
      icon: <FaRegLightbulb />,
      title: 'Abordagem',
      description: 'Foco em resultados com técnicas baseadas em evidências científicas'
    }
  ];

  return (
    <section id="sobre" className="py-24 bg-[#F8F3F0] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#D4B4A8]/20 via-[#4A3B36]/5 to-transparent rounded-full filter blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#D4B4A8]/20 via-[#4A3B36]/5 to-transparent rounded-full filter blur-3xl"
        />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Coluna da Imagem */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative w-full md:w-4/5 lg:w-full aspect-square mx-auto"
          >
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="absolute -inset-4 bg-gradient-to-br from-[#D4B4A8] to-[#4A3B36] rounded-2xl transform -rotate-6"
            />
            <motion.div
              className="relative w-full h-full overflow-hidden rounded-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500"
            >
              <motion.img
                src={aboutImage}
                alt="Késia Almeida - Psicóloga"
                className="w-full h-full object-cover object-[center_15%]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              />
            </motion.div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8 lg:pl-8"
          >
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-5xl font-bold text-[#4A3B36] mb-6 relative inline-block"
              >
                {aboutContent.title || 'Sobre Mim'}
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-[#D4B4A8] to-[#4A3B36]"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                />
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[#6B5C57] text-lg leading-relaxed mb-8"
              >
                {aboutContent.description || 'Sou psicóloga clínica com formação em Terapia Cognitivo-Comportamental. Minha abordagem é centrada no cliente, buscando proporcionar um ambiente acolhedor e seguro para o processo terapêutico.'}
              </motion.p>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-1 gap-6">
              {highlights.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="flex items-start space-x-4">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#D4B4A8] to-[#4A3B36] flex items-center justify-center text-white text-2xl"
                    >
                      {item.icon}
                    </motion.div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#4A3B36] group-hover:text-[#D4B4A8] transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="text-[#6B5C57] mt-2">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.a
              href="https://api.whatsapp.com/send/?phone=5562982615656"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2 w-fit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaWhatsapp className="text-xl" />
              Agende sua Consulta
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
